import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Phone, DoorOpen, Key, Lock, Wrench, Shield, 
  ChevronRight, Clock, CheckCircle, ArrowLeft
} from "lucide-react";
import HeroBanner from "../components/HeroBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/services`).then(res => res.json()),
      fetch(`${API_URL}/api/config`).then(res => res.json())
    ])
      .then(([servicesData, configData]) => {
        setServices(servicesData);
        setConfig(configData);
        setLoading(false);
      })
      .catch(console.error);
  }, []);

  const getServiceIcon = (iconName) => {
    const icons = {
      DoorOpen: DoorOpen,
      Lock: Lock,
      Key: Key,
      KeyRound: Key,
      Wrench: Wrench,
      Cylinder: Lock,
      ShieldAlert: Shield
    };
    const IconComponent = icons[iconName] || Lock;
    return <IconComponent className="w-8 h-8" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0f2744]"></div>
      </div>
    );
  }

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <HeroBanner
        title="Dépannage Serrures Fichet"
        subtitle="Tous types d'interventions sur vos serrures et portes équipées de systèmes Fichet. Techniciens qualifiés, intervention rapide 7j/7."
        highlightText="Fichet"
        showFeatures={false}
        showCTA={true}
        config={config}
        variant="compact"
        breadcrumb={
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Accueil
          </Link>
        }
      />

      {/* Trust bar */}
      <section className="bg-white py-6 border-b border-gray-100">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-8 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-5 h-5 text-amber-500" />
              Intervention en 30 min
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Devis gratuit
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Shield className="w-5 h-5 text-blue-500" />
              Garantie pièces et main d'œuvre
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#0f2744] mb-4">Nos prestations détaillées</h2>
            <p className="text-gray-600">Cliquez sur un service pour en savoir plus</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.slug}`}
                className="card group flex gap-6 p-6 hover:border-l-4 hover:border-l-orange-500 transition-all"
                data-testid={`service-${service.slug}`}
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                    {getServiceIcon(service.icon)}
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-[#0f2744] mb-2 group-hover:text-orange-600 transition-colors">
                    {service.name}
                  </h2>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-orange-600 font-medium">
                    En savoir plus
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing info */}
      <section className="section-alt">
        <div className="container-custom">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#0f2744] mb-4">
                  Tarifs transparents
                </h2>
                <p className="text-gray-600 mb-6">
                  Nos tarifs dépendent du type d'intervention, de l'heure et de la complexité. 
                  Un devis détaillé vous est toujours communiqué avant intervention.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Devis gratuit et sans engagement</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Pas de frais cachés</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Paiement CB, espèces ou virement</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Facture pour assurance</span>
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-[#0f2744] mb-4">Tarifs indicatifs</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Ouverture de porte simple</span>
                    <span className="font-semibold">à partir de 89€</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Ouverture porte blindée</span>
                    <span className="font-semibold">à partir de 150€</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Changement cylindre</span>
                    <span className="font-semibold">à partir de 120€</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Réparation serrure</span>
                    <span className="font-semibold">sur devis</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  * Tarifs indicatifs en heures ouvrables. Majoration possible les nuits, 
                  week-ends et jours fériés.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-[#0f2744] to-[#1a3a5c] py-12 text-white">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Besoin d'un devis rapidement ?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {config && (
              <a
                href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                className="btn-cta"
                data-testid="services-cta-phone"
              >
                <Phone className="w-5 h-5 mr-2" />
                {config.phone}
              </a>
            )}
            <Link
              to="/contact"
              className="btn-outline border-white text-white hover:bg-white hover:text-[#0f2744]"
            >
              Demander un devis en ligne
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
